import {
    optionsConfirmationDialog_title,
    optionsConfirmationDialog_subtitle,
    optionsConfirmationDialog_save,
    optionsConfirmationDialog_discard,
    optionsConfirmationDialog_cancel,
} from './OptionConfirmationDialog.locstring.json';
import loc from 'owa-localize';
import { observer } from 'mobx-react';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';

import * as React from 'react';
import { showModal, OwaModalProps } from 'owa-modal';
import { assertNever } from 'owa-assert';

const styles = require('./OptionConfirmationDialog.scss');

export const enum DismissState {
    Save,
    Discard,
    Cancel,
    Dismiss,
}

export type OptionConfirmationDialogProps = OwaModalProps<DismissState> & {
    shouldDisableSaveButton: boolean;
};

/**
 * A Dialog to show three options: save your current changes, discard them, or go back and edit them.
 * This is implemented rather than using owa-confirm-dialog because that system doesn't support three options,
 * and would add unneeded complexity to that package for a one-off scenario.
 * Additionally, this may end up styled differently in the future, with stacked full-width buttons, rather than
 * using the default Dialog styling of a row of footer buttons.
 */
@observer
class OptionConfirmationDialog extends React.Component<OptionConfirmationDialogProps, {}> {
    render() {
        return (
            <Dialog
                isOpen={true}
                type={DialogType.normal}
                onDismiss={this.onDismiss}
                className={styles.dialog}
                title={loc(optionsConfirmationDialog_title)}
                subText={loc(optionsConfirmationDialog_subtitle)}>
                <DialogFooter>
                    <PrimaryButton
                        className={styles.footerButton}
                        text={loc(optionsConfirmationDialog_save)}
                        onClick={this.onSave}
                        disabled={this.props.shouldDisableSaveButton}
                    />
                    <DefaultButton
                        className={styles.footerButton}
                        text={loc(optionsConfirmationDialog_discard)}
                        onClick={this.onDiscard}
                    />
                    <DefaultButton
                        className={styles.footerButton}
                        text={loc(optionsConfirmationDialog_cancel)}
                        onClick={this.onCancel}
                    />
                </DialogFooter>
            </Dialog>
        );
    }
    private onSave = () => {
        this.props.onDismiss(DismissState.Save);
    };

    private onDiscard = () => {
        this.props.onDismiss(DismissState.Discard);
    };

    private onDismiss = () => {
        this.props.onDismiss(DismissState.Dismiss);
    };

    private onCancel = () => {
        this.props.onDismiss(DismissState.Cancel);
    };
}

export interface ConfirmOptionNavigationProps {
    onSave: () => void;
    onDiscard: () => void;
    onCancel?: () => void;
    shouldDisableSaveButton?: boolean;
}

export default function confirmOptionNavigation(props: ConfirmOptionNavigationProps) {
    const WrappedComponent = ({ onDismiss }: OwaModalProps<DismissState>) => (
        <OptionConfirmationDialog
            onDismiss={onDismiss}
            shouldDisableSaveButton={props.shouldDisableSaveButton}
        />
    );
    const [modalPromise] = showModal<DismissState>(WrappedComponent);
    modalPromise.then(dismissState => {
        switch (dismissState) {
            case DismissState.Save:
                props.onSave();
                break;
            case DismissState.Discard:
                props.onDiscard();
                break;
            case DismissState.Cancel:
                if (props.onCancel) {
                    props.onCancel();
                }
                break;
            case DismissState.Dismiss:
                break;
            default:
                assertNever(dismissState);
        }
    });
}
